import { ReactComponent as IconCheck } from "@common_assets/svg/check.svg"
import { ReactComponent as IconStake } from "@common_assets/svg/stake.svg"
import { ReactComponent as IconUser } from "@common_assets/svg/user-solid.svg"
import { Box, Skeleton } from "@mui/material"
import { useQueryAdminDashboardTeachersKpi } from "api/reactQuery/queries/adminDashboard"
import { useTranslation } from "react-i18next"
import {
  IDashboardPeriodParams,
  IDashboardTeachersKpi,
} from "ts/interfaces/AdminDashboard"

import DashboardKpiCard from "./DashboardKpiCard"
import DashboardSectionBody from "./DashboardSectionBody"

const formatCount = (value: number | null) =>
  value === null ? "—" : Math.round(value).toLocaleString("en-US")
const formatPercent = (value: number | null) =>
  value === null ? "—" : `${Math.round(value * 100)}%`

export const kpiGridSx = {
  display: "grid",
  gap: 1.5,
  gridTemplateColumns: { mobile: "1fr", tablet: "repeat(3, 1fr)" },
  gridAutoRows: "minmax(147px, auto)",
}

export const KpiSkeleton = () => (
  <Box sx={kpiGridSx} aria-busy="true">
    {Array.from({ length: 3 }, (_, index) => (
      <Skeleton
        key={index}
        variant="rounded"
        data-testid="kpi-card-skeleton"
        sx={{ height: "auto", borderRadius: "16px" }}
      />
    ))}
  </Box>
)

const KpiCards = ({ kpi }: { kpi: IDashboardTeachersKpi }) => {
  const { t } = useTranslation()

  return (
    <Box sx={kpiGridSx}>
      <DashboardKpiCard
        accent="mockup.primary60"
        icon={<IconUser width={20} height={20} />}
        iconBgColor="mockup.primary90"
        iconColor="mockup.primary60"
        value={formatCount(kpi.active_teachers)}
        label={t("adminDashboard.kpiTitleTeachers")}
        caption={t("adminDashboard.teachersUsingClassBank")}
      />
      <DashboardKpiCard
        accent="mockup.success50"
        icon={<IconCheck width={24} height={24} />}
        iconBgColor="mockup.success90"
        iconColor="mockup.success40"
        value={formatPercent(kpi.sent_transactions_share)}
        label={t("adminDashboard.kpiTitleActivity")}
        caption={t("adminDashboard.sentTransactionsThisPeriod")}
      />
      <DashboardKpiCard
        accent="mockup.secondary60"
        icon={<IconStake width={32} height={32} />}
        iconBgColor="mockup.secondary90"
        iconColor="mockup.secondary60"
        value={formatCount(kpi.avg_transactions_per_teacher)}
        label={t("adminDashboard.kpiTitleTransactions")}
        caption={t("adminDashboard.avgTransactionsPerTeacher")}
      />
    </Box>
  )
}

const TeachersKpiSummary = ({ params }: { params: IDashboardPeriodParams }) => {
  const query = useQueryAdminDashboardTeachersKpi(params)
  const kpi = query.data?.data

  return (
    <DashboardSectionBody queries={[query]} skeleton={<KpiSkeleton />}>
      {kpi && <KpiCards kpi={kpi} />}
    </DashboardSectionBody>
  )
}

export default TeachersKpiSummary
